import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import MarkupField from '../../components/MarkupField';
import { useDispatch, useSelector } from 'react-redux';
import { createCategory, updateCategory, selectCategories } from './categoriesSlice';
import { useToast } from '../../components/ui/Toast';
import Modal from '../../components/ui/Modal';
import './CategoryModal.css';

const CategoryModal = ({ isOpen, onClose, category, parentCategory, onSuccess }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const categories = useSelector(selectCategories);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [parentId, setParentId] = useState(null);
  // Phase 4.A.6 follow-on — optional middle layer in the markup hierarchy.
  const [markupPercent, setMarkupPercent] = useState('');
  const [storePricingDefaults, setStorePricingDefaults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isEditing = !!category;

  useEffect(() => {
    if (isOpen) {
      if (category) {
        // Editing existing category
        setName(category.name || '');
        setDescription(category.description || '');
        setImage(category.image || '');
        setIsPublic(category.isPublic !== false);
        setParentId(category.parentId || null);
        setMarkupPercent(category.markupPercent != null ? String(category.markupPercent) : '');
      } else {
        // Creating new category
        setName('');
        setDescription('');
        setImage('');
        setIsPublic(true);
        setParentId(parentCategory?.id || null);
        setMarkupPercent('');
      }
    }
  }, [isOpen, category, parentCategory]);

  // Phase 4.A.6 polish — fetch store-wide pricing defaults so the
  // MarkupField shows "Inherits X% from store" instead of an empty box.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/merchants/pricing-defaults');
        if (!cancelled) setStorePricingDefaults(data);
      } catch (err) {
        console.warn('[CategoryModal] pricing-defaults fetch failed', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      if (response.ok && data.url) {
        setImage(data.url);
        toast.success('Image uploaded');
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setLoading(true);

    try {
      const markupParsed = markupPercent === '' ? null : parseFloat(markupPercent);
      if (isEditing) {
        await dispatch(updateCategory({
          id: category.id,
          data: {
            name: name.trim(),
            description: description.trim() || null,
            image: image || null,
            isPublic,
            parentId,
            markupPercent: markupParsed
          }
        })).unwrap();
        toast.success('Category updated successfully');
      } else {
        await dispatch(createCategory({
          name: name.trim(),
          description: description.trim() || null,
          image: image || null,
          isPublic,
          parentId,
          markupPercent: markupParsed
        })).unwrap();
        toast.success('Category created successfully');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error || `Failed to ${isEditing ? 'update' : 'create'} category`);
    } finally {
      setLoading(false);
    }
  };

  // Get available parent categories (exclude self and own children when editing)
  const getParentOptions = () => {
    if (!categories) return [];

    return categories.filter(cat => {
      // Can't be own parent
      if (isEditing && cat.id === category.id) return false;
      // Only show root categories as parent options
      if (cat.parentId) return false;
      return true;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Category' : (parentCategory ? `Add Subcategory to ${parentCategory.name}` : 'New Category')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="category-modal-form">
        {/* Name */}
        <div className="form-group">
          <label htmlFor="cat-name">Category Name *</label>
          <input
            id="cat-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Electronics, Clothing, Food"
            className="form-input"
            required
          />
        </div>

        {/* Description */}
        <div className="form-group">
          <label htmlFor="cat-description">Description</label>
          <textarea
            id="cat-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this category"
            className="form-textarea"
            rows={3}
          />
        </div>

        {/* Parent Category (only for root categories being edited, or when creating without preset parent) */}
        {(!parentCategory && !category?.parentId) && (
          <div className="form-group">
            <label htmlFor="cat-parent">Parent Category</label>
            <select
              id="cat-parent"
              value={parentId || ''}
              onChange={(e) => setParentId(e.target.value || null)}
              className="form-select"
            >
              <option value="">None (Root Category)</option>
              {getParentOptions().map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <p className="form-hint">Subcategories appear under their parent category</p>
          </div>
        )}

        {/* Image Upload */}
        <div className="form-group">
          <label>Category Image</label>
          <div className="image-upload-area">
            {image ? (
              <div className="image-preview">
                <img src={image} alt="Category" />
                <button
                  type="button"
                  className="image-remove-btn"
                  onClick={handleRemoveImage}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <label className="image-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
                <div className="image-upload-content">
                  {uploading ? (
                    <div className="uploading-spinner" />
                  ) : (
                    <>
                      <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>Upload Image</span>
                    </>
                  )}
                </div>
              </label>
            )}
          </div>
        </div>

        {/* Markup % — Phase 4.A.6 follow-on. Middle layer in the
            pricing hierarchy (Product → Category → Store). Inherits the
            store default by default; merchant can override per category. */}
        <div className="form-group">
          <MarkupField
            inherited={
              storePricingDefaults?.defaultMarkupPercent != null
                ? Number(storePricingDefaults.defaultMarkupPercent)
                : null
            }
            inheritedFrom={
              storePricingDefaults?.defaultMarkupPercent != null ? 'store' : null
            }
            value={markupPercent}
            onChange={setMarkupPercent}
            onConfigureClick={() => window.open('/settings?tab=general#pricing-defaults', '_blank')}
          />
        </div>

        {/* Visibility */}
        <div className="form-group category-form-checkbox">
          <label>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            Visible on storefront
          </label>
          <small>Hidden categories are only visible to you</small>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={loading || !name.trim()}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CategoryModal;
